import { APP_CONFIG, Env } from '../../environment';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { IGeolocation } from '../../model/models/device/geolocation.interface';
import { ForeignIpGeolocationSession } from './foreign-ip-geolocation-session';

export interface IForeignIpTestOverrides {
  enabled: boolean;
  timeZone: string;
  fixMode: 'device' | 'none' | 'custom';
  latitude: number | null;
  longitude: number | null;
  isMock: boolean;
}

export const FOREIGN_IP_TEST_OVERRIDE_DEFAULTS: IForeignIpTestOverrides = {
  enabled: false,
  timeZone: '',
  fixMode: 'device',
  latitude: null,
  longitude: null,
  isMock: false,
};

export const FOREIGN_IP_TEST_TIME_ZONES = [
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Tehran',
  'Asia/Kolkata',
  'Europe/Moscow',
  'Europe/Kiev',
  'America/Havana',
  'Europe/London',
];

export const FOREIGN_IP_TEST_LOCATIONS: { text: string; latitude: number; longitude: number }[] = [
  { text: 'Lake Elsinore, US (33.6654, -117.3847)', latitude: 33.6654, longitude: -117.3847 },
  { text: 'Tehran, IR (35.6892, 51.389)', latitude: 35.6892, longitude: 51.389 },
  { text: 'Paris, FR (48.8566, 2.3522)', latitude: 48.8566, longitude: 2.3522 },
  { text: 'Kolkata, IN (22.5726, 88.3639)', latitude: 22.5726, longitude: 88.3639 },
  { text: 'Mid-ocean (0, 0)', latitude: 0, longitude: 0 },
];

export class ForeignIpGeolocationTestOverrides {
  static current: IForeignIpTestOverrides = { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS };
  static ready: Promise<void> = Promise.resolve();
  private static savedThisSession = false;

  static isActive(): boolean {
    return APP_CONFIG.env !== Env.PROD && ForeignIpGeolocationTestOverrides.current.enabled;
  }

  static timeZone(): string | undefined {
    const o = ForeignIpGeolocationTestOverrides.current;
    return ForeignIpGeolocationTestOverrides.isActive() && o.timeZone ? o.timeZone : undefined;
  }

  static fix(): IGeolocation | null | undefined {
    const o = ForeignIpGeolocationTestOverrides.current;
    if (!ForeignIpGeolocationTestOverrides.isActive() || o.fixMode === 'device') {
      return undefined;
    }
    const hasCustomPair = typeof o.latitude === 'number' && typeof o.longitude === 'number';
    if (o.fixMode === 'none' || !hasCustomPair) {
      return null;
    }
    return { latitude: o.latitude, longitude: o.longitude, accuracy: 10, isMock: o.isMock, timestamp: Date.now() };
  }

  static async load(storageProvider: StorageProvider) {
    if (APP_CONFIG.env === Env.PROD) {
      ForeignIpGeolocationTestOverrides.current = { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS };
      return;
    }
    ForeignIpGeolocationTestOverrides.ready = storageProvider.getJson(StorageType.ForeignIpTestOverrides).then((stored) => {
      if (ForeignIpGeolocationTestOverrides.savedThisSession) {
        return;
      }
      ForeignIpGeolocationTestOverrides.current = ForeignIpGeolocationTestOverrides.pickKnown(stored);
    }, () => undefined);
    await ForeignIpGeolocationTestOverrides.ready;
  }

  private static pickKnown(stored: any): IForeignIpTestOverrides {
    const result: IForeignIpTestOverrides = { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS };
    if (stored && typeof stored === 'object') {
      Object.keys(FOREIGN_IP_TEST_OVERRIDE_DEFAULTS).forEach((key) => {
        if (stored[key] !== undefined) {
          result[key] = stored[key];
        }
      });
    }
    return result;
  }

  static async save(storageProvider: StorageProvider, overrides: IForeignIpTestOverrides) {
    ForeignIpGeolocationTestOverrides.savedThisSession = true;
    ForeignIpGeolocationTestOverrides.current = { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS, ...overrides };
    ForeignIpGeolocationSession.lastFix = null;
    await storageProvider.setJson(StorageType.ForeignIpTestOverrides, ForeignIpGeolocationTestOverrides.current);
  }

  static async reset(storageProvider: StorageProvider) {
    await ForeignIpGeolocationTestOverrides.save(storageProvider, { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS });
  }
}



// WEBPACK FOOTER //
// ./src/providers/foreign-ip-geolocation/foreign-ip-geolocation-test-overrides.ts